import React, { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Company autocomplete filter.
 *
 * Default: trigger + absolutely positioned popover (matches FilterDropdown).
 * `inline`: accordion section in normal flow with no click-outside
 * dismissal, for the mobile filters sheet.
 */
function CompanyFilter({ companies = [], selected = [], onChange, inline = false }) {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Filter companies based on input
  const filteredCompanies = companies.filter((company) =>
    company.toLowerCase().includes(inputValue.toLowerCase()) &&
    !selected.includes(company)
  ).slice(0, 10); // Limit to 10 suggestions

  // Handle input change
  const handleInputChange = useCallback((e) => {
    setInputValue(e.target.value);
    setHighlightedIndex(-1);
  }, []);

  // Handle selecting a company
  const handleSelect = useCallback((company) => {
    onChange([...selected, company]);
    setInputValue('');
    setHighlightedIndex(-1);
    // Inline (mobile sheet): don't re-focus, it would pop the phone keyboard.
    if (!inline) inputRef.current?.focus();
  }, [selected, onChange, inline]);

  // Handle removing a selected company
  const handleRemove = useCallback((company, e) => {
    e.stopPropagation();
    onChange(selected.filter((c) => c !== company));
  }, [selected, onChange]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (filteredCompanies.length === 0) {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredCompanies.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          handleSelect(filteredCompanies[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  }, [filteredCompanies, highlightedIndex, handleSelect]);

  // Close dropdown when clicking outside (popover mode only)
  useEffect(() => {
    if (inline) return undefined;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
        setInputValue('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [inline]);

  // Clear all selections
  const handleClearAll = useCallback((e) => {
    e.stopPropagation();
    onChange([]);
    setInputValue('');
    if (!inline) setIsOpen(false);
  }, [onChange, inline]);

  // Toggle dropdown open/close
  const handleTriggerClick = useCallback(() => {
    setIsOpen(!isOpen);
    if (!isOpen && !inline) {
      // Focus input when opening (popover only; inline would pop the phone keyboard)
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen, inline]);

  const hasSelection = selected.length > 0;

  return (
    <div className={`company-filter ${inline ? 'filter-dropdown--inline' : ''}`} ref={containerRef}>
      {/* Trigger button - matches FilterDropdown style */}
      <button
        className={`filter-dropdown-trigger ${isOpen ? 'active' : ''} ${hasSelection ? 'has-selection' : ''}`}
        onClick={handleTriggerClick}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span>Company</span>
        {hasSelection && (
          <span className="filter-dropdown-badge">{selected.length}</span>
        )}
        <svg
          className="filter-dropdown-chevron"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Dropdown menu */}
      <div className={`company-filter-menu ${isOpen ? 'open' : ''}`}>
        {/* Selected companies at top with remove buttons */}
        {hasSelection && (
          <div className="company-filter-selected">
            {selected.map((company) => (
              <div key={company} className="company-filter-selected-item">
                <span className="company-filter-selected-label">{company}</span>
                <button
                  className="company-filter-selected-remove"
                  onClick={(e) => handleRemove(company, e)}
                  aria-label={`Remove ${company}`}
                >
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="currentColor">
                    <path d="M9.53 3.53a.75.75 0 0 0-1.06-1.06L6 4.94 3.53 2.47a.75.75 0 0 0-1.06 1.06L4.94 6 2.47 8.47a.75.75 0 1 0 1.06 1.06L6 7.06l2.47 2.47a.75.75 0 1 0 1.06-1.06L7.06 6l2.47-2.47Z"/>
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Search input */}
        <div className="company-filter-search">
          <input
            ref={inputRef}
            type="text"
            className="company-filter-search-input"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Search companies..."
            aria-label="Search companies"
            aria-autocomplete="list"
          />
        </div>

        {/* Autocomplete suggestions */}
        {filteredCompanies.length > 0 && (
          <ul className="company-filter-suggestions" role="listbox">
            {filteredCompanies.map((company, index) => (
              <li
                key={company}
                role="option"
                aria-selected={index === highlightedIndex}
                className={`company-filter-suggestion ${index === highlightedIndex ? 'highlighted' : ''}`}
                onClick={() => handleSelect(company)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                {company}
              </li>
            ))}
          </ul>
        )}

        {/* No results message */}
        {inputValue && filteredCompanies.length === 0 && (
          <div className="company-filter-no-results">
            No companies found
          </div>
        )}

        {/* Clear all button */}
        {hasSelection && (
          <button className="filter-dropdown-clear" onClick={handleClearAll}>
            Clear All
          </button>
        )}
      </div>
    </div>
  );
}

export default CompanyFilter;
